import React, { useCallback, useEffect, useMemo, useState, ChangeEvent, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox,
  Alert,
  Tooltip
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PreviewIcon from '@mui/icons-material/Preview';
import { resolveAbsoluteCctvStreamUrl, pickCctvLivePlayback } from '../../utils/cctvStreamUrl';
import { usePlaybackConfig } from '../../hooks/usePlaybackConfig';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { standardCctvApi } from '../../services/standardCctvApi';
import api from '../../services/api';
import { ApiResponse } from '../../types/api';
import { Cctv, CctvFormData, CctvUpdate, CctvCreate } from '../../types/domain';
import { CctvStatus } from '../../types/cctv';
import CctvPreview from './CctvPreview';
import {
  CCTV_PURPOSE_OPTIONS,
  CCTV_MOUNT_TYPE_OPTIONS,
  CCTV_DETECTION_SCENARIO_OPTIONS,
  DEFAULT_DETECTION_SCENARIO,
  DEFAULT_DETECTION_PURPOSE,
  filterDetectionScenariosForProfile,
  filterDetectionPurposesForProfile,
  defaultPurposeForProfile,
  scenarioForPurpose,
  purposeForScenario,
  resolveDetectionForFacilityProfile,
  SCENARIO_PROFILE_OPTIONS,
  normalizeScenarioProfile,
  CCTV_STATUS_OPTIONS,
  STATUS_MAPPING,
} from '../../constants/cctv';
import { formatDate } from '../../utils/dateUtils';
import { notification } from 'antd';
import axios from 'axios';
import { Form } from 'antd';
import { convertFormDataToCreateData, convertFormDataToUpdateData } from '../../utils/cctvUtils';
import { convertStatusToEnglish, convertStatusToKorean } from '../../constants/cctv';
import { AI_DETECTION_HINTS } from '../../constants/aiDetection';
import {
  applyPoolDetectionPreset,
  inferPoolDetectionPreset,
  type PoolDetectionPresetId,
} from '../../constants/detectionPresets';
import {
  applyCctvMotionOverridePreset,
  inferCctvMotionOverridePreset,
  type MotionRiskPresetId,
} from '../../constants/motionRiskPresets';
import DetectionLevelPicker from '../system/DetectionLevelPicker';
import {
  POOL_RISK_MOTION_HINTS,
  POOL_RISK_MOTION_CCTV_SECTION_NOTE,
  POOL_RISK_MOTION_CCTV_TUNING_SUMMARY,
  POOL_RISK_MOTION_HELPER_PROPS,
  mergePoolRiskMotionDefaults,
  type PoolRiskMotionDefaults,
} from '../../constants/poolRiskMotion';
import {
  DEFAULT_RISK_LAND_THRESHOLDS,
  mergeRiskLandThresholds,
  type RiskLandThresholds,
} from '../../constants/riskLandThresholds';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers';
import RiskZoneEditor from './RiskZoneEditor';
import PoolCctvThresholdUnifyCard from '../pool/PoolCctvThresholdUnifyCard';

/** 신규 CCTV 등록 시 구역 기본 (영상 위에서 그리면 JSON으로 채워짐) */
const CCTV_RISK_NEW_DEFAULTS = {
  zonesJson: '[]',
  specialZonesJson: '[]',
} as const;

/** 위험 구역 [x1,y1,x2,y2] 입력란 옆·아래 안내용 */
const CCTV_RISK_ZONES_COORDS_GUIDE =
  '[x1, y1, x2, y2] — 이 CCTV 영상의 픽셀 좌표. 화면 왼쪽 위가 (0,0), x는 오른쪽으로, y는 아래로 증가. ' +
  'x1·y1과 x2·y2는 사각형의 서로 맞은편 모서리면 되며(어느 모서리를 먼저 써도 됨), 여러 구역은 [[…],[…]]처럼 배열로 나열.';

function parseZonesJsonField(raw: string | undefined | null): number[][] | null {
  try {
    const t = (raw || '').trim();
    if (!t) return null;
    const z = JSON.parse(t);
    return Array.isArray(z) ? z : null;
  } catch {
    return null;
  }
}

// CCTV 폼 데이터 타입 정의
interface CCTVFormErrors {
  name?: string;
  location?: string;
  ip_address?: string;
  port?: string;
  code?: string;
  stream_url?: string;
  status?: string;
  last_maintenance_date?: string;
  next_maintenance_date?: string;
}

export interface CCTVManagementProps {
  poolId?: string;
  poolCode?: string;
  /** 시설 현장 게이트웨이 사용 여부 (없으면 미리보기에서 API 조회) */
  siteGatewayEnabled?: boolean;
  /** 시설 기본 YOLO 신뢰도 하한 (없으면 시스템 기본 승계) */
  poolAiDetectionConf?: number | null;
  /** 시설 기본 YOLO 연속 프레임 확정 (없으면 시스템 기본 승계) */
  poolAiDetectionMinConsecutiveHits?: number | null;
  /** 시설 수정 폼의 Motion 위험 감지 기본값 (스위치 ON 시 이 카메라가 따름) */
  poolRiskMotionDefaults?: PoolRiskMotionDefaults | null;
  /** 시설 육상 임계 부분 오버라이드 (특별관리구역 체류 시설 기본값 표시용) */
  poolRiskLandThresholds?: Partial<RiskLandThresholds> | Record<string, number> | null;
  /** 시설 관제 프로필 — 위험 시나리오 Select 필터 */
  scenarioProfile?: string | null;
  onSnackbarMessage: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const CCTVManagement: React.FC<CCTVManagementProps> = ({
  poolId,
  poolCode,
  siteGatewayEnabled,
  poolAiDetectionConf,
  poolAiDetectionMinConsecutiveHits,
  poolRiskMotionDefaults,
  poolRiskLandThresholds,
  scenarioProfile,
  onSnackbarMessage,
}) => {
  const { centralGo2rtcAvailable, centralGo2rtcStreams } = usePlaybackConfig();
  // CCTV 관련 상태
  const [cctvs, setCCTVs] = useState<Cctv[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [editingCCTV, setEditingCCTV] = useState<Cctv | null>(null);
  const [cctvFormData, setCCTVFormData] = useState<CctvFormData>({
    name: '',
    status: 'ACTIVE',
    location: '',
    code: '',
    stream_url: '',
    playback_url: '',
    purpose: '관제 모니터링',
    mount_type: 'overhead',
    rotate_180: false,
    detection_scenario: DEFAULT_DETECTION_SCENARIO,
    detection_purpose: DEFAULT_DETECTION_PURPOSE,
    ai_detector_model: '',
    is_active: true,
    last_maintenance_date: null,
    next_maintenance_date: null,
    pool_id: Number(poolId) || 0,
    risk_use_pool_motion_defaults: true,
    risk_detection_sensitivity: 0.75,
    risk_detection_stillness_sec: 10,
    risk_detection_zones_json: CCTV_RISK_NEW_DEFAULTS.zonesJson,
    special_management_zones_json: CCTV_RISK_NEW_DEFAULTS.specialZonesJson,
    special_zone_dwell_sec: '',
    ai_detection_conf: '',
    ai_detection_min_consecutive_hits: '',
    monitor_filter_strength: '',
    tracker_type: 'native',
  });
  const [cctvFormErrors, setCCTVFormErrors] = useState<CCTVFormErrors>({});
  const [dataRefreshTrigger, setDataRefreshTrigger] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [previewCctv, setPreviewCctv] = useState<Cctv | null>(null);
  const [selectedCctv, setSelectedCctv] = useState<Cctv | null>(null);

  const [cctvList, setCctvList] = useState<Cctv[]>([]);
  const [form] = Form.useForm<CctvFormData>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [cctvDetectionPreset, setCctvDetectionPreset] = useState<
    PoolDetectionPresetId | 'pool_default'
  >('pool_default');
  const [cctvMotionPreset, setCctvMotionPreset] = useState<MotionRiskPresetId | 'pool_motion'>(
    'pool_motion'
  );
  const [yoloModelFiles, setYoloModelFiles] = useState<string[]>([]);
  const [systemLandBase, setSystemLandBase] = useState<RiskLandThresholds>(
    DEFAULT_RISK_LAND_THRESHOLDS
  );

  const resolvedPoolMotion = mergePoolRiskMotionDefaults(poolRiskMotionDefaults ?? undefined);

  /** 이 시설에 적용되는 특별관리구역 체류 초 (시스템⊂시설) */
  const facilitySpecialZoneDwellSec = useMemo(() => {
    return mergeRiskLandThresholds(systemLandBase, poolRiskLandThresholds)
      .special_zone_dwell_sec;
  }, [systemLandBase, poolRiskLandThresholds]);

  const facilityDetectionConfHint = useMemo(() => {
    if (poolAiDetectionConf == null) {
      return '미설정 (시스템 기본 승계)';
    }
    return Number(poolAiDetectionConf).toFixed(2);
  }, [poolAiDetectionConf]);

  const facilityDetectionHitsHint = useMemo(() => {
    if (poolAiDetectionMinConsecutiveHits == null) {
      return '미설정 (시스템 기본 승계)';
    }
    return String(Number(poolAiDetectionMinConsecutiveHits));
  }, [poolAiDetectionMinConsecutiveHits]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<{ risk_thresholds?: Partial<RiskLandThresholds> }>(
          '/api/settings/system'
        );
        if (cancelled) return;
        setSystemLandBase(mergeRiskLandThresholds(data?.risk_thresholds));
      } catch {
        if (!cancelled) setSystemLandBase(DEFAULT_RISK_LAND_THRESHOLDS);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poolId]);

  const purposeSelectOptions = useMemo(
    () =>
      filterDetectionPurposesForProfile(
        scenarioProfile,
        cctvFormData.detection_purpose
      ),
    [scenarioProfile, cctvFormData.detection_purpose]
  );

  const scenarioSelectOptions = useMemo(
    () =>
      filterDetectionScenariosForProfile(
        scenarioProfile,
        CCTV_DETECTION_SCENARIO_OPTIONS,
        cctvFormData.detection_scenario
      ),
    [scenarioProfile, cctvFormData.detection_scenario]
  );

  const riskZoneEditorStreamUrl = useMemo(() => {
    const rawStream = (cctvFormData.stream_url || '').trim();
    const rawPlayback = (
      cctvFormData.playback_url ||
      editingCCTV?.playback_url ||
      ''
    ).trim();
    const cctvId = editingCCTV?.id;
    const hasRealId =
      typeof cctvId === 'number'
        ? cctvId > 0
        : cctvId != null && Number(cctvId) > 0;
    const poolIdNum =
      Number(cctvFormData.pool_id || poolId) || editingCCTV?.pool_id || undefined;

    // 저장된 CCTV + 게이트웨이 ON: 관제 playback(HLS) 우선 — RTSP 스냅샷 깜빡임 방지
    if (hasRealId && siteGatewayEnabled && rawPlayback.toLowerCase().includes('.m3u8')) {
      return (
        resolveAbsoluteCctvStreamUrl(
          {
            id: typeof cctvId === 'number' ? cctvId : Number(cctvId),
            pool_id: poolIdNum,
            stream_url: rawStream,
            playback_url: rawPlayback,
            webrtc_url: editingCCTV?.webrtc_url,
          },
          true,
          true
        ) || rawPlayback
      );
    }

    // 등록(미저장): playback/hls-proxy 없음 → stream_url(RTSP·직접 URL)만 사용 (스냅샷)
    if (!hasRealId) {
      return rawStream;
    }

    // 구역 편집 배경: 서버/로컬 스냅샷 — RTSP stream_url 우선 (playback m3u8·hls-proxy 부적합)
    if (
      rawStream &&
      (rawStream.toLowerCase().startsWith('rtsp://') ||
        rawStream.toLowerCase().startsWith('rtsps://'))
    ) {
      return rawStream;
    }

    // 수정: 브라우저 재생 가능한 HLS(playback) 우선, 없으면 stream_url
    const raw =
      rawPlayback.toLowerCase().includes('.m3u8') ? rawPlayback : rawStream;
    if (!raw) return '';
    if (raw.toLowerCase().includes('.m3u8')) {
      return (
        resolveAbsoluteCctvStreamUrl(
          {
            id: typeof cctvId === 'number' ? cctvId : Number(cctvId),
            pool_id: poolIdNum,
            stream_url: rawStream,
            playback_url: rawPlayback,
          },
          true,
          true
        ) || raw
      );
    }
    return raw;
  }, [
    cctvFormData.stream_url,
    cctvFormData.playback_url,
    cctvFormData.pool_id,
    editingCCTV?.id,
    editingCCTV?.pool_id,
    editingCCTV?.playback_url,
    poolId,
    siteGatewayEnabled,
    editingCCTV?.webrtc_url,
  ]);

  const riskZoneEditorWhepUrl = useMemo(() => {
    const cctvId = editingCCTV?.id;
    const hasRealId =
      typeof cctvId === 'number' ? cctvId > 0 : cctvId != null && Number(cctvId) > 0;
    if (!hasRealId || !siteGatewayEnabled) return '';
    const streamKey =
      editingCCTV?.pool_id && cctvId ? `p${editingCCTV.pool_id}_c${cctvId}` : '';
    const live = pickCctvLivePlayback(
      {
        id: cctvId,
        pool_id: editingCCTV?.pool_id,
        stream_url: editingCCTV?.stream_url,
        playback_url: editingCCTV?.playback_url,
        webrtc_url: editingCCTV?.webrtc_url,
      },
      true,
      Boolean(siteGatewayEnabled),
      centralGo2rtcAvailable,
      centralGo2rtcStreams
    );
    if (live.mode !== 'webrtc' || !live.webrtcUrl) return '';
    if (streamKey && !centralGo2rtcStreams.includes(streamKey)) return '';
    return live.webrtcUrl;
  }, [editingCCTV, siteGatewayEnabled, centralGo2rtcAvailable, centralGo2rtcStreams]);

  const handlePreviewStream = (cctv: Cctv) => {
    const hasAny = Boolean(
      (cctv.stream_url || '').trim() ||
        ((cctv as { playback_url?: string }).playback_url || '').trim() ||
        ((cctv as { url?: string }).url || '').trim()
    );
    if (!hasAny) {
      onSnackbarMessage('재생할 스트림 URL(playback_url 또는 stream_url)이 없음.', 'error');
      return;
    }
    setPreviewCctv(cctv);
  };

  /**
   * 수정/추가 폼의 「미리보기」.
   * 수정: 실제 CCTV id → LAN 에서 hls-proxy 가능.
   * 등록(미저장): id 없음 → RTSP/입력 URL 스냅샷·직접 재생 (프록시 불가).
   */
  const handlePreviewStreamUrl = (streamUrl: string) => {
    const trimmed = (streamUrl || '').trim();
    if (!trimmed) {
      onSnackbarMessage('미리보기할 스트림 URL을 입력', 'error');
      return;
    }

    const poolIdNum = Number(cctvFormData.pool_id || poolId) || 0;
    const realId = Number(editingCCTV?.id);
    const hasRealId = Number.isFinite(realId) && realId > 0;

    if (hasRealId && editingCCTV) {
      setPreviewCctv({
        ...editingCCTV,
        stream_url: trimmed,
        // 게이트웨이 OFF 이면 playback 잔존본을 쓰지 않음 (목록과 동일)
        playback_url: siteGatewayEnabled
          ? editingCCTV.playback_url || ''
          : '',
        pool_id: poolIdNum || editingCCTV.pool_id,
      });
      return;
    }

    // 신규(미저장): id 생략(<=0 과 동일하게 프록시 스킵). RTSP는 CctvPreview 스냅샷 경로.
    const lower = trimmed.toLowerCase();
    const isHls = lower.includes('.m3u8');
    setPreviewCctv({
      name: cctvFormData.name || '새 CCTV',
      code: cctvFormData.code || 'CCTV-PREVIEW',
      stream_url: trimmed,
      // HLS를 stream에 넣은 경우만 playback에도 동일 URL (프록시 없이 직접 시도)
      playback_url: isHls ? trimmed : '',
      status: (cctvFormData.status as CctvStatus) || 'ACTIVE',
      is_active: true,
      pool_id: poolIdNum > 0 ? poolIdNum : undefined,
      location: cctvFormData.location || '',
      purpose: cctvFormData.purpose || '관제 모니터링',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as Cctv);
  };

  const handleClosePreview = () => {
    setPreviewCctv(null);
  };

  const handleEdit = async (cctv: Cctv) => {
    try {
      console.log('CCTV 수정 시작:', cctv.id, '시설 ID:', poolId);
      
      const numericPoolId = Number(poolId);
      if (isNaN(numericPoolId) || numericPoolId <= 0) {
        onSnackbarMessage('CCTV 수정 실패: 유효한 시설 ID가 필요', 'error');
        return;
      }
      
      if (cctv.id) {
        // 선택된 CCTV 데이터 가져오기
        const response = await standardCctvApi.getCctv(numericPoolId, cctv.id);
        
        if (response.success && response.data) {
          console.log('서버에서 가져온 CCTV 데이터:', response.data);
          
          const formattedData: Cctv = {
            ...response.data,
            created_at: response.data.created_at || new Date().toISOString(),
            updated_at: response.data.updated_at || new Date().toISOString()
          };
          
          setSelectedCctv(formattedData);
          setEditingCCTV(formattedData);
          setCCTVFormData({
            id: formattedData.id,
            name: formattedData.name,
            code: formattedData.code,
            status: formattedData.status as CctvStatus,
            location: formattedData.location,
            purpose: formattedData.purpose || '관제 모니터링',
            stream_url: formattedData.stream_url,
            is_active: formattedData.is_active,
            last_maintenance_date: formattedData.last_maintenance,
            next_maintenance_date: formattedData.next_maintenance,
            pool_id: formattedData.pool_id,
            manufacturer: formattedData.manufacturer,
            description: formattedData.description,
            model: formattedData.model,
            ip_address: formattedData.ip_address,
            port: formattedData.port,
            username: formattedData.username,
            password: formattedData.password,
            created_at: formattedData.created_at,
            updated_at: formattedData.updated_at,
            alert_type: formattedData.alert_type,
            alert_severity: formattedData.alert_severity,
            alert_description: formattedData.alert_description,
            alert_created_at: formattedData.alert_created_at,
            risk_use_pool_motion_defaults:
              formattedData.risk_use_pool_motion_defaults !== false,
            risk_detection_sensitivity:
              formattedData.risk_detection_sensitivity != null
                ? formattedData.risk_detection_sensitivity
                : 0.75,
            risk_detection_stillness_sec:
              formattedData.risk_detection_stillness_sec != null
                ? formattedData.risk_detection_stillness_sec
                : 10,
            risk_detection_zones_json:
              formattedData.risk_detection_zones != null &&
              formattedData.risk_detection_zones.length > 0
                ? JSON.stringify(formattedData.risk_detection_zones)
                : CCTV_RISK_NEW_DEFAULTS.zonesJson,
            special_management_zones_json:
              formattedData.special_management_zones != null &&
              formattedData.special_management_zones.length > 0
                ? JSON.stringify(formattedData.special_management_zones)
                : CCTV_RISK_NEW_DEFAULTS.specialZonesJson,
            special_zone_dwell_sec:
              formattedData.special_zone_dwell_sec != null &&
              formattedData.special_zone_dwell_sec !== undefined
                ? formattedData.special_zone_dwell_sec
                : '',
            ai_detection_conf:
              formattedData.ai_detection_conf != null &&
              formattedData.ai_detection_conf !== undefined
                ? formattedData.ai_detection_conf
                : '',
            ai_detection_min_consecutive_hits:
              formattedData.ai_detection_min_consecutive_hits != null &&
              formattedData.ai_detection_min_consecutive_hits !== undefined
                ? formattedData.ai_detection_min_consecutive_hits
                : '',
            monitor_filter_strength:
              formattedData.monitor_filter_strength != null &&
              formattedData.monitor_filter_strength !== undefined
                ? formattedData.monitor_filter_strength
                : '',
            tracker_type: String(formattedData.tracker_type || 'native'),
          });
          setOpenEditDialog(true);
        } else {
          console.error('CCTV 데이터를 가져오는데 실패했습니다.', response.message);
          onSnackbarMessage(response.message || 'CCTV 정보를 불러오기 불가', 'error');
        }
      } else {
        console.error('유효하지 않은 CCTV ID:', cctv.id);
        onSnackbarMessage('CCTV 정보를 확인 불가', 'error');
      }
    } catch (error) {
      console.error('CCTV 데이터 로딩 중 오류 발생:', error);
      onSnackbarMessage('CCTV 정보를 불러오는 중 오류 발생', 'error');
    }
  };

  // CCTV 목록 로드
  const loadCCTVs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[loadCCTVs] 시작: poolId=', poolId, ', poolCode=', poolCode);

      let cctvData;
      const numericPoolId = Number(poolId);
      if (!isNaN(numericPoolId) && numericPoolId > 0) {
        cctvData = await standardCctvApi.getCctvsByPoolId(numericPoolId);
      } else if (poolCode) {
        const poolResponse = await standardCctvApi.getPoolIdByCode(poolCode);
        if (poolResponse && poolResponse.id) {
          cctvData = await standardCctvApi.getCctvsByPoolId(poolResponse.id);
        }
      }

      if (cctvData) {
        const formattedCctvs = cctvData.map(cctv => ({
          ...cctv,
          created_at: cctv.created_at || new Date().toISOString(),
          updated_at: cctv.updated_at || new Date().toISOString()
        }));
        setCCTVs(formattedCctvs);
      }
    } catch (error) {
      console.error('[loadCCTVs] CCTV API 호출 오류:', error);
      setError('CCTV 목록을 불러오기 실패');
      onSnackbarMessage('CCTV 목록을 불러오기 실패', 'error');
    } finally {
      setLoading(false);
    }
  }, [poolId, poolCode, onSnackbarMessage]);

  const fetchCCTVs = async () => {
    try {
      if (!poolCode) {
        setError('시설 코드가 설정되지 않음');
        return;
      }
      
      const response = await standardCctvApi.getCctvsByPoolCode(poolCode);
      if (response.success) {
        setCCTVs(response.data);
      }
    } catch (error) {
      console.error('Error fetching CCTVs:', error);
    }
  };

  const handleCreateCctv = async () => {
    try {
      const numericPoolId = Number(poolId);
      if (isNaN(numericPoolId) || numericPoolId <= 0) {
        setError('유효한 시설 ID가 필요');
        return;
      }

      // 필수 필드 검증
      if (!cctvFormData.name || !cctvFormData.code || !cctvFormData.location) {
        setError('이름, 코드, 위치 필수 입력 항목');
        return;
      }

      // 필수 필드만 포함한 데이터 준비
      const createData: CctvCreate = {
        name: cctvFormData.name,
        code: cctvFormData.code,
        status: cctvFormData.status,
        location: cctvFormData.location,
        purpose: cctvFormData.purpose || '관제 모니터링',
        mount_type: cctvFormData.mount_type === 'oblique' ? 'oblique' : 'overhead',
        rotate_180: Boolean(cctvFormData.rotate_180),
          detection_scenario:
          cctvFormData.detection_scenario || DEFAULT_DETECTION_SCENARIO,
        detection_purpose:
          cctvFormData.detection_purpose ||
          defaultPurposeForProfile(scenarioProfile),
        ai_detector_model:
          String(cctvFormData.ai_detector_model || '').trim() || null,
        stream_url: cctvFormData.stream_url || '',
        is_active: cctvFormData.is_active,
        pool_id: numericPoolId,
        ip_address: cctvFormData.ip_address || null,
        manufacturer: cctvFormData.manufacturer || null,
        description: cctvFormData.description || null,
        model: cctvFormData.model || null,
        last_maintenance: cctvFormData.last_maintenance_date || null,
        next_maintenance: cctvFormData.next_maintenance_date || null,
        created_at: cctvFormData.created_at || new Date().toISOString(),
        updated_at: cctvFormData.updated_at || new Date().toISOString(),
        alert_type: cctvFormData.alert_type || null,
        alert_severity: cctvFormData.alert_severity || null,
        alert_description: cctvFormData.alert_description || null,
        alert_created_at: cctvFormData.alert_created_at || null,
        risk_use_pool_motion_defaults: cctvFormData.risk_use_pool_motion_defaults !== false,
        risk_detection_sensitivity:
          cctvFormData.risk_detection_sensitivity === '' ||
          cctvFormData.risk_detection_sensitivity === undefined
            ? 0.75
            : Number(cctvFormData.risk_detection_sensitivity),
        risk_detection_stillness_sec:
          cctvFormData.risk_detection_stillness_sec === '' ||
          cctvFormData.risk_detection_stillness_sec === undefined
            ? 10
            : Number(cctvFormData.risk_detection_stillness_sec),
        risk_detection_waterline_y: null,
        risk_detection_zones: parseZonesJsonField(cctvFormData.risk_detection_zones_json),
        special_management_zones: parseZonesJsonField(
          cctvFormData.special_management_zones_json
        ),
        special_zone_dwell_sec:
          cctvFormData.special_zone_dwell_sec === '' ||
          cctvFormData.special_zone_dwell_sec === undefined ||
          cctvFormData.special_zone_dwell_sec === null
            ? null
            : Number(cctvFormData.special_zone_dwell_sec),
        ai_detection_conf:
          cctvFormData.ai_detection_conf === '' || cctvFormData.ai_detection_conf === undefined
            ? null
            : Number(cctvFormData.ai_detection_conf),
        ai_detection_min_consecutive_hits:
          cctvFormData.ai_detection_min_consecutive_hits === '' ||
          cctvFormData.ai_detection_min_consecutive_hits === undefined
            ? null
            : Number(cctvFormData.ai_detection_min_consecutive_hits),
        monitor_filter_strength:
          cctvFormData.monitor_filter_strength === '' ||
          cctvFormData.monitor_filter_strength === undefined
            ? null
            : Number(cctvFormData.monitor_filter_strength),
        tracker_type: String(cctvFormData.tracker_type || 'native'),
      };

      console.log('서버에 전송하는 CCTV 생성 데이터:', createData);
      const response = await standardCctvApi.createCctv(numericPoolId, createData);
      
      if (response.success) {
        setError(null);
        setCCTVFormData({
          name: '',
          code: '',
          status: 'ACTIVE' as CctvStatus,
          location: '',
          purpose: '',
          mount_type: 'overhead',
          rotate_180: false,
          detection_scenario: DEFAULT_DETECTION_SCENARIO,
    detection_purpose: DEFAULT_DETECTION_PURPOSE,
          ai_detector_model: '',
          stream_url: '',
          playback_url: '',
          is_active: true,
          last_maintenance_date: null,
          next_maintenance_date: null,
          pool_id: numericPoolId,
          risk_detection_sensitivity: 0.75,
          risk_detection_stillness_sec: 10,
          risk_detection_zones_json: CCTV_RISK_NEW_DEFAULTS.zonesJson,
          special_management_zones_json: CCTV_RISK_NEW_DEFAULTS.specialZonesJson,
          special_zone_dwell_sec: '',
          ai_detection_conf: '',
          ai_detection_min_consecutive_hits: '',
          monitor_filter_strength: '',
          tracker_type: 'native',
        });
        await loadCCTVs();
        onSnackbarMessage('CCTV가 생성됨', 'success');
        setOpenCreateDialog(false);
      } else {
        onSnackbarMessage(response.message || 'CCTV 생성 중 오류 발생', 'error');
      }
    } catch (error) {
      console.error('CCTV 생성 중 오류 발생:', error);
      onSnackbarMessage('CCTV 생성 중 오류 발생', 'error');
    }
  };

  const handleUpdateCctv = async (cctvFormData: CctvFormData) => {
    try {
      setIsSubmitting(true);
      
      const numericPoolId = Number(poolId);
      if (isNaN(numericPoolId) || numericPoolId <= 0) {
        throw new Error('유효한 시설 ID가 필요');
      }

      if (!editingCCTV?.id) {
        throw new Error('CCTV ID가 필요');
      }

      const updateData = convertFormDataToUpdateData(cctvFormData);
      console.log('CCTV 업데이트 데이터:', {
        poolId: numericPoolId,
        cctvId: editingCCTV.id,
        updateData
      });
      
      const response = await standardCctvApi.updateCctv(numericPoolId, editingCCTV.id, updateData);
      
      if (response.success) {
        onSnackbarMessage('CCTV 정보가 업데이트 완료', 'success');
        setOpenEditDialog(false);
        await loadCCTVs();
      } else {
        throw new Error(response.message || 'CCTV 정보 업데이트에 실패');
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // CCTV 삭제
  const handleDeleteCCTV = async (id: number) => {
    try {
      if (!window.confirm('정말로 이 CCTV를 삭제 확인')) {
        return;
      }
      
      // poolId 확인
      const numericPoolId = Number(poolId);
      if (isNaN(numericPoolId) || numericPoolId <= 0) {
        onSnackbarMessage('CCTV 삭제 실패: 유효한 시설 ID가 필요', 'error');
        return;
      }
      
      const response = await standardCctvApi.deleteCctv(numericPoolId, id);
      if (response?.success !== false) {
        setCCTVs(prev => prev.filter(cctv => cctv.id !== id));
        onSnackbarMessage('CCTV가 삭제 완료', 'success');
        await loadCCTVs();
      } else {
        setError(response.message || 'CCTV 삭제 실패');
        onSnackbarMessage(response.message || 'CCTV 삭제에 실패', 'error');
      }
    } catch (error: any) {
      console.error('CCTV 삭제 중 오류:', error);
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message;
      onSnackbarMessage(
        typeof detail === 'string' && detail.trim()
          ? detail
          : 'CCTV 삭제 중 오류 발생',
        'error',
      );
    }
  };

  // CCTV 폼 다이얼로그 닫기
  const handleCloseCCTVDialog = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setEditingCCTV(null);
    setCCTVFormData({
      name: '',
      code: '',
      status: 'ACTIVE' as CctvStatus,
      location: '',
      purpose: '',
      mount_type: 'overhead',
      rotate_180: false,
      detection_scenario: DEFAULT_DETECTION_SCENARIO,
    detection_purpose: DEFAULT_DETECTION_PURPOSE,
      ai_detector_model: '',
      stream_url: '',
      playback_url: '',
      is_active: true,
      last_maintenance_date: null,
      next_maintenance_date: null,
      pool_id: 0,
      risk_detection_sensitivity: 0.75,
      risk_detection_stillness_sec: 10,
      risk_detection_zones_json: CCTV_RISK_NEW_DEFAULTS.zonesJson,
      special_management_zones_json: CCTV_RISK_NEW_DEFAULTS.specialZonesJson,
      special_zone_dwell_sec: '',
      ai_detection_conf: '',
      ai_detection_min_consecutive_hits: '',
      monitor_filter_strength: '',
      tracker_type: 'native',
    });
    setCCTVFormErrors({});
  };

  // CCTV 폼 다이얼로그 열기
  const handleOpenCCTVDialog = (cctv?: Cctv) => {
    if (cctv) {
      const cctvWithDefaults: Cctv = {
        ...cctv,
        created_at: cctv.created_at || new Date().toISOString(),
        updated_at: cctv.updated_at || new Date().toISOString()
      };
      setEditingCCTV(cctvWithDefaults);
      setCCTVFormData({
        id: cctvWithDefaults.id,
        name: cctvWithDefaults.name,
        code: cctvWithDefaults.code,
        status: cctvWithDefaults.status,
        location: cctvWithDefaults.location,
        purpose: cctvWithDefaults.purpose || '관제 모니터링',
        mount_type: cctvWithDefaults.mount_type === 'oblique' ? 'oblique' : 'overhead',
        rotate_180: Boolean(cctvWithDefaults.rotate_180),
        ...(() => {
          const resolved = resolveDetectionForFacilityProfile(
            scenarioProfile,
            cctvWithDefaults.detection_purpose,
            cctvWithDefaults.detection_scenario
          );
          if (resolved.remapped) {
            onSnackbarMessage(
              '시설 관제 프로필과 맞지 않는 검출 설정(예: 수영장 익사)을 이 시설 기본값으로 맞춤. 저장 시 반영됨.',
              'info'
            );
          }
          return {
            detection_purpose: resolved.purpose,
            detection_scenario: resolved.scenario,
          };
        })(),
        ai_detector_model: cctvWithDefaults.ai_detector_model || '',
        stream_url: cctvWithDefaults.stream_url,
        playback_url: cctvWithDefaults.playback_url || '',
        is_active: cctvWithDefaults.is_active,
        last_maintenance_date: cctvWithDefaults.last_maintenance || null,
        next_maintenance_date: cctvWithDefaults.next_maintenance || null,
        pool_id: cctvWithDefaults.pool_id,
        manufacturer: cctvWithDefaults.manufacturer,
        description: cctvWithDefaults.description,
        model: cctvWithDefaults.model,
        ip_address: cctvWithDefaults.ip_address,
        port: cctvWithDefaults.port,
        username: cctvWithDefaults.username,
        password: cctvWithDefaults.password,
        created_at: cctvWithDefaults.created_at,
        updated_at: cctvWithDefaults.updated_at,
        alert_type: cctvWithDefaults.alert_type,
        alert_severity: cctvWithDefaults.alert_severity,
        alert_description: cctvWithDefaults.alert_description,
        alert_created_at: cctvWithDefaults.alert_created_at,
        risk_use_pool_motion_defaults:
          cctvWithDefaults.risk_use_pool_motion_defaults !== false,
        risk_detection_sensitivity:
          cctvWithDefaults.risk_detection_sensitivity != null
            ? cctvWithDefaults.risk_detection_sensitivity
            : 0.75,
        risk_detection_stillness_sec:
          cctvWithDefaults.risk_detection_stillness_sec != null
            ? cctvWithDefaults.risk_detection_stillness_sec
            : 10,
        risk_detection_zones_json:
          cctvWithDefaults.risk_detection_zones != null &&
          cctvWithDefaults.risk_detection_zones.length > 0
            ? JSON.stringify(cctvWithDefaults.risk_detection_zones)
            : CCTV_RISK_NEW_DEFAULTS.zonesJson,
        special_management_zones_json:
          cctvWithDefaults.special_management_zones != null &&
          cctvWithDefaults.special_management_zones.length > 0
            ? JSON.stringify(cctvWithDefaults.special_management_zones)
            : CCTV_RISK_NEW_DEFAULTS.specialZonesJson,
        special_zone_dwell_sec:
          cctvWithDefaults.special_zone_dwell_sec != null &&
          cctvWithDefaults.special_zone_dwell_sec !== undefined
            ? cctvWithDefaults.special_zone_dwell_sec
            : '',
        ai_detection_conf:
          cctvWithDefaults.ai_detection_conf != null &&
          cctvWithDefaults.ai_detection_conf !== undefined
            ? cctvWithDefaults.ai_detection_conf
            : '',
        ai_detection_min_consecutive_hits:
          cctvWithDefaults.ai_detection_min_consecutive_hits != null &&
          cctvWithDefaults.ai_detection_min_consecutive_hits !== undefined
            ? cctvWithDefaults.ai_detection_min_consecutive_hits
            : '',
        monitor_filter_strength:
          cctvWithDefaults.monitor_filter_strength != null &&
          cctvWithDefaults.monitor_filter_strength !== undefined
            ? cctvWithDefaults.monitor_filter_strength
            : '',
        tracker_type: String(cctvWithDefaults.tracker_type || 'native'),
      });
      const confVal = cctvWithDefaults.ai_detection_conf;
      const hitsVal = cctvWithDefaults.ai_detection_min_consecutive_hits;
      if (confVal == null || confVal === undefined) {
        setCctvDetectionPreset('pool_default');
      } else {
        setCctvDetectionPreset(
          inferPoolDetectionPreset(Number(confVal), Number(hitsVal ?? 0))
        );
      }
      if (cctvWithDefaults.risk_use_pool_motion_defaults !== false) {
        setCctvMotionPreset('pool_motion');
      } else {
        setCctvMotionPreset(
          inferCctvMotionOverridePreset(
            cctvWithDefaults.risk_detection_sensitivity,
            cctvWithDefaults.risk_detection_stillness_sec
          )
        );
      }
      setOpenEditDialog(true);
    } else {
      setEditingCCTV(null);
      const numericPoolId = Number(poolId);
      setCCTVFormData({
        name: '',
        code: '',
        status: 'ACTIVE' as CctvStatus,
        location: '',
        purpose: '관제 모니터링',
        mount_type: 'overhead',
        rotate_180: false,
        detection_purpose: defaultPurposeForProfile(scenarioProfile),
        detection_scenario:
          scenarioForPurpose(defaultPurposeForProfile(scenarioProfile)) ||
          DEFAULT_DETECTION_SCENARIO,
        ai_detector_model: '',
        stream_url: '',
        playback_url: '',
        is_active: true,
        last_maintenance_date: null,
        next_maintenance_date: null,
        pool_id: !isNaN(numericPoolId) && numericPoolId > 0 ? numericPoolId : 0,
        risk_use_pool_motion_defaults: true,
        risk_detection_sensitivity: 0.75,
        risk_detection_stillness_sec: 10,
        risk_detection_zones_json: CCTV_RISK_NEW_DEFAULTS.zonesJson,
        special_management_zones_json: CCTV_RISK_NEW_DEFAULTS.specialZonesJson,
        special_zone_dwell_sec: '',
        ai_detection_conf: '',
        ai_detection_min_consecutive_hits: '',
        monitor_filter_strength: '',
        tracker_type: 'native',
      });
      setCctvDetectionPreset('pool_default');
      setCctvMotionPreset('pool_motion');
      setOpenCreateDialog(true);
    }
  };

  // CCTV 폼 변경 핸들러
  const handleCCTVChange = (field: keyof CctvFormData, value: any) => {
    setCCTVFormData((prevData) => {
      const updatedData = { ...prevData };
      
      // 필드별 처리
      switch (field) {
        case 'status':
          // 상태값이 한글이면 영문으로 변환
          if (typeof value === 'string') {
            const englishStatus = STATUS_MAPPING[value as keyof typeof STATUS_MAPPING] || 'ACTIVE';
            updatedData.status = englishStatus as CctvStatus;
          } else {
            updatedData.status = 'ACTIVE';
          }
          break;
        case 'is_active':
          updatedData.is_active = Boolean(value);
          break;
        case 'rotate_180':
          updatedData.rotate_180 = Boolean(value);
          break;
        case 'risk_use_pool_motion_defaults':
          updatedData.risk_use_pool_motion_defaults = Boolean(value);
          break;
        case 'last_maintenance_date':
        case 'next_maintenance_date':
          updatedData[field] = value || null;
          break;
        case 'risk_detection_sensitivity':
        case 'risk_detection_stillness_sec':
        case 'ai_detection_conf':
        case 'ai_detection_min_consecutive_hits':
        case 'monitor_filter_strength':
        case 'tracker_type':
          updatedData[field] = value;
          break;
        case 'risk_detection_zones_json':
        case 'special_management_zones_json':
          updatedData[field] = value != null ? String(value) : '';
          break;
        case 'special_zone_dwell_sec':
          updatedData.special_zone_dwell_sec = value;
          break;
        case 'detection_purpose': {
          const purpose = String(value || '');
          updatedData.detection_purpose = purpose;
          if (purpose && purpose !== 'custom') {
            const mapped = scenarioForPurpose(purpose);
            if (mapped) {
              updatedData.detection_scenario = mapped;
            }
          }
          break;
        }
        case 'detection_scenario': {
          updatedData.detection_scenario = String(value || '');
          // 고급: 시나리오만 바꾸면 용도를 custom으로 (매핑 일치 시 유지)
          const curPurpose = String(updatedData.detection_purpose || '');
          if (curPurpose && curPurpose !== 'custom') {
            const expected = scenarioForPurpose(curPurpose);
            if (expected && expected !== updatedData.detection_scenario) {
              updatedData.detection_purpose = 'custom';
            }
          }
          break;
        }
        default:
          // @ts-ignore - 타입 오류 무시
          updatedData[field] = value !== undefined && value !== null ? String(value) : '';
          break;
      }
      
      return updatedData;
    });

    // 필수 필드인 경우 유효성 검사 업데이트
    if (['name', 'status', 'location', 'code', 'stream_url'].includes(field)) {
      setCCTVFormErrors(prev => {
        const newErrors = { ...prev };
        newErrors[field as keyof CCTVFormErrors] = !value ? '필수 입력 항목' : '';
        return newErrors;
      });
    }
  };

  // CCTV 폼 유효성 검사
  const validateCCTVForm = (): boolean => {
    const errors: CCTVFormErrors = {};
    let isValid = true;

    if (!cctvFormData.name || cctvFormData.name.trim() === '') {
      errors.name = '이름 필수 입력 항목';
      isValid = false;
    }

    if (!cctvFormData.code || cctvFormData.code.trim() === '') {
      errors.code = '코드 필수 입력 항목';
      isValid = false;
    }

    if (!cctvFormData.stream_url || cctvFormData.stream_url.trim() === '') {
      errors.stream_url = '스트림 URL은 필수 입력 항목';
      isValid = false;
    }

    // 모든 오류 메시지가 문자열인지 확인
    Object.keys(errors).forEach(key => {
      const errorKey = key as keyof CCTVFormErrors;
      if (errors[errorKey] && typeof errors[errorKey] !== 'string') {
        errors[errorKey] = '유효하지 않은 값';
      }
    });

    setCCTVFormErrors(errors);
    return isValid;
  };

  // 안전한 메시지 표시 함수 추가
  const safeMessage = (msg: any): string => {
    if (typeof msg === 'string') return msg;
    if (msg === null || msg === undefined) return '';
    if (typeof msg === 'object') {
      try {
        return JSON.stringify(msg);
      } catch (e) {
        return '객체 메시지';
      }
    }
    return String(msg);
  };

  // 에러 메시지 처리 함수 추가
  const errorHandler = (error: any) => {
    console.error('CCTV 관리 오류:', error);
    let errorMessage = '알 수 없는 오류';
    
    if (error && typeof error === 'object') {
      if ('response' in error && error.response) {
        const responseData = error.response.data;
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (typeof responseData === 'object') {
          if ('detail' in responseData) {
            errorMessage = Array.isArray(responseData.detail) 
              ? responseData.detail.map((err: any) => err.msg || err.message || '알 수 없는 오류').join(', ')
              : (responseData.detail?.msg || responseData.detail?.message || '알 수 없는 오류');
          } else if ('message' in responseData) {
            errorMessage = responseData.message;
          } else if ('msg' in responseData) {
            errorMessage = responseData.msg;
          }
        }
      } else if ('message' in error) {
        errorMessage = error.message;
      }
    }
    
    setError(errorMessage);
    onSnackbarMessage(errorMessage, 'error');
  };

  // CCTV 폼 제출 핸들러 수정
  const handleCCTVSubmit = async () => {
    try {
      const numericPoolId = Number(poolId);
      if (isNaN(numericPoolId) || numericPoolId <= 0) {
        onSnackbarMessage('유효한 시설 ID가 필요', 'error');
        return;
      }

      if (editingCCTV) {
        // 수정 모드
        await handleUpdateCctv(cctvFormData);
      } else {
        // 생성 모드
        // 필수 필드 검증
        if (!cctvFormData.code || cctvFormData.code.trim() === '') {
          onSnackbarMessage('CCTV 코드 필수 입력 항목', 'error');
          return;
        }

        if (!cctvFormData.name || cctvFormData.name.trim() === '') {
          onSnackbarMessage('CCTV 이름 필수 입력 항목', 'error');
          return;
        }

        if (!cctvFormData.location || cctvFormData.location.trim() === '') {
          onSnackbarMessage('CCTV 위치 필수 입력 항목', 'error');
          return;
        }

        const createData: CctvCreate = {
          name: cctvFormData.name,
          location: cctvFormData.location,
          status: cctvFormData.status,
          code: cctvFormData.code,
          stream_url: cctvFormData.stream_url || '',
          is_active: cctvFormData.is_active,
          pool_id: numericPoolId,
          purpose: cctvFormData.purpose || '관제 모니터링',
          mount_type: cctvFormData.mount_type === 'oblique' ? 'oblique' : 'overhead',
          rotate_180: Boolean(cctvFormData.rotate_180),
          detection_scenario:
          cctvFormData.detection_scenario || DEFAULT_DETECTION_SCENARIO,
        detection_purpose:
          cctvFormData.detection_purpose ||
          defaultPurposeForProfile(scenarioProfile),
        ai_detector_model:
          String(cctvFormData.ai_detector_model || '').trim() || null,
          ip_address: cctvFormData.ip_address || null,
          manufacturer: cctvFormData.manufacturer || null,
          description: cctvFormData.description || null,
          model: cctvFormData.model || null,
          last_maintenance: cctvFormData.last_maintenance_date || null,
          next_maintenance: cctvFormData.next_maintenance_date || null,
          created_at: cctvFormData.created_at || new Date().toISOString(),
          updated_at: cctvFormData.updated_at || new Date().toISOString(),
          alert_type: cctvFormData.alert_type || null,
          alert_severity: cctvFormData.alert_severity || null,
          alert_description: cctvFormData.alert_description || null,
          alert_created_at: cctvFormData.alert_created_at || null,
          risk_use_pool_motion_defaults: cctvFormData.risk_use_pool_motion_defaults !== false,
          risk_detection_sensitivity:
            cctvFormData.risk_detection_sensitivity === '' ||
            cctvFormData.risk_detection_sensitivity === undefined
              ? 0.75
              : Number(cctvFormData.risk_detection_sensitivity),
          risk_detection_stillness_sec:
            cctvFormData.risk_detection_stillness_sec === '' ||
            cctvFormData.risk_detection_stillness_sec === undefined
              ? 10
              : Number(cctvFormData.risk_detection_stillness_sec),
          risk_detection_waterline_y: null,
          risk_detection_zones: parseZonesJsonField(cctvFormData.risk_detection_zones_json),
          special_management_zones: parseZonesJsonField(
            cctvFormData.special_management_zones_json
          ),
          special_zone_dwell_sec:
            cctvFormData.special_zone_dwell_sec === '' ||
            cctvFormData.special_zone_dwell_sec === undefined ||
            cctvFormData.special_zone_dwell_sec === null
              ? null
              : Number(cctvFormData.special_zone_dwell_sec),
          ai_detection_conf:
            cctvFormData.ai_detection_conf === '' || cctvFormData.ai_detection_conf === undefined
              ? null
              : Number(cctvFormData.ai_detection_conf),
          ai_detection_min_consecutive_hits:
            cctvFormData.ai_detection_min_consecutive_hits === '' ||
            cctvFormData.ai_detection_min_consecutive_hits === undefined
              ? null
              : Number(cctvFormData.ai_detection_min_consecutive_hits),
          monitor_filter_strength:
            cctvFormData.monitor_filter_strength === '' ||
            cctvFormData.monitor_filter_strength === undefined
              ? null
              : Number(cctvFormData.monitor_filter_strength),
          tracker_type: String(cctvFormData.tracker_type || 'native'),
        };

        console.log('서버로 전송되는 CCTV 생성 데이터:', createData);
        const response = await standardCctvApi.createCctv(numericPoolId, createData);
        
        if (response.success) {
          handleCloseCCTVDialog();
          await loadCCTVs();
          onSnackbarMessage(response.message || 'CCTV가 생성됨', 'success');
        } else {
          onSnackbarMessage(response.message || 'CCTV 생성 중 오류 발생', 'error');
        }
      }
    } catch (error) {
      console.error('Error submitting CCTV:', error);
      onSnackbarMessage('CCTV 처리 중 오류 발생', 'error');
    }
  };

  // CCTV 유형을 한글로 표시하는 함수
  const getCCTVTypeDisplay = (camera_type?: string) => {
    switch (camera_type) {
      case 'safety':
        return '안전용';
      case 'management':
        return '관리용';
      default:
        return '일반용';
    }
  };

  // CCTV 데이터가 변경될 때마다 로드
  useEffect(() => {
    // 컴포넌트가 마운트되거나 풀 ID/코드가 변경될 때 데이터 로드
    if (poolId || poolCode) {
      loadCCTVs();
    }
  }, [poolId, poolCode, loadCCTVs]);  // 올바른 의존성 추가

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ files?: string[] }>('/api/settings/yolo-model-files');
        const files = Array.isArray(res?.files) ? res.files : [];
        if (!cancelled) setYoloModelFiles(files.filter(Boolean));
      } catch {
        if (!cancelled) setYoloModelFiles([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 선택된 CCTV가 변경되면 폼 데이터 업데이트
  useEffect(() => {
    if (editingCCTV) {
      // 날짜 형식 변환 함수
      const formatDateForInput = (dateString: string | null | undefined) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          return date.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm 형식으로 변환
        } catch (e) {
          console.error('날짜 변환 오류:', e);
          return '';
        }
      };

      // 폼 데이터를 현재 CCTV 데이터로 초기화
      setCCTVFormData({
        id: editingCCTV.id,
        name: editingCCTV.name,
        pool_id: editingCCTV.pool_id,
        status: editingCCTV.status,
        location: editingCCTV.location,
        ip_address: editingCCTV.ip_address,
        port: editingCCTV.port,
        username: editingCCTV.username,
        password: editingCCTV.password,
        code: editingCCTV.code || '',
        stream_url: editingCCTV.stream_url || '',
        playback_url: editingCCTV.playback_url || '',
        is_active: editingCCTV.is_active || true,
        purpose: editingCCTV.purpose || '',
        mount_type: editingCCTV.mount_type === 'oblique' ? 'oblique' : 'overhead',
        rotate_180: Boolean(editingCCTV.rotate_180),
        ...(() => {
          const resolved = resolveDetectionForFacilityProfile(
            scenarioProfile,
            editingCCTV.detection_purpose,
            editingCCTV.detection_scenario
          );
          return {
            detection_purpose: resolved.purpose,
            detection_scenario: resolved.scenario,
          };
        })(),
        ai_detector_model: editingCCTV.ai_detector_model || '',
        alert_type: editingCCTV.alert_type,
        alert_severity: editingCCTV.alert_severity,
        alert_description: editingCCTV.alert_description,
        alert_created_at: formatDateForInput(editingCCTV.alert_created_at),
        last_maintenance_date: formatDateForInput(editingCCTV.last_maintenance),
        next_maintenance_date: formatDateForInput(editingCCTV.next_maintenance),
        model: editingCCTV.model || '',
        manufacturer: editingCCTV.manufacturer || '',
        description: editingCCTV.description || '',
        created_at: formatDateForInput(editingCCTV.created_at),
        updated_at: formatDateForInput(editingCCTV.updated_at),
        risk_detection_sensitivity:
          editingCCTV.risk_detection_sensitivity != null
            ? editingCCTV.risk_detection_sensitivity
            : 0.75,
        risk_detection_stillness_sec:
          editingCCTV.risk_detection_stillness_sec != null
            ? editingCCTV.risk_detection_stillness_sec
            : 10,
        risk_detection_zones_json:
          editingCCTV.risk_detection_zones != null &&
          editingCCTV.risk_detection_zones.length > 0
            ? JSON.stringify(editingCCTV.risk_detection_zones)
            : CCTV_RISK_NEW_DEFAULTS.zonesJson,
        special_management_zones_json:
          editingCCTV.special_management_zones != null &&
          editingCCTV.special_management_zones.length > 0
            ? JSON.stringify(editingCCTV.special_management_zones)
            : CCTV_RISK_NEW_DEFAULTS.specialZonesJson,
        special_zone_dwell_sec:
          editingCCTV.special_zone_dwell_sec != null &&
          editingCCTV.special_zone_dwell_sec !== undefined
            ? editingCCTV.special_zone_dwell_sec
            : '',
        ai_detection_conf:
          editingCCTV.ai_detection_conf != null && editingCCTV.ai_detection_conf !== undefined
            ? editingCCTV.ai_detection_conf
            : '',
        ai_detection_min_consecutive_hits:
          editingCCTV.ai_detection_min_consecutive_hits != null &&
          editingCCTV.ai_detection_min_consecutive_hits !== undefined
            ? editingCCTV.ai_detection_min_consecutive_hits
            : '',
        monitor_filter_strength:
          editingCCTV.monitor_filter_strength != null &&
          editingCCTV.monitor_filter_strength !== undefined
            ? editingCCTV.monitor_filter_strength
            : '',
        tracker_type: String(editingCCTV.tracker_type || 'native'),
      });
    }
  }, [editingCCTV]);

  // CCTV 폼 다이얼로그 렌더링
  const renderCCTVDialog = () => {
    // 상태값 변환 함수 - 이름 수정 및 함수 내용 유지
    const getStatusDisplayValue = (status: CctvStatus | string): string => {
      if (!status) return '정상';
      try {
        const koreanStatus = convertStatusToKorean(String(status));
        return koreanStatus || '정상';
      } catch (e) {
        console.error('상태값 변환 오류:', e);
        return '정상';
      }
    };

    return (
      <Dialog 
        open={openCreateDialog || openEditDialog} 
        onClose={handleCloseCCTVDialog}
        maxWidth="md" 
        fullWidth
        disableEnforceFocus={!!previewCctv}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{editingCCTV?.id ? 'CCTV 수정' : 'CCTV 추가'}</Typography>
            <IconButton size="small" onClick={handleCloseCCTVDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            시설 관제 프로필:{' '}
            <strong>
              {SCENARIO_PROFILE_OPTIONS.find(
                (o) => o.value === normalizeScenarioProfile(scenarioProfile)
              )?.label || normalizeScenarioProfile(scenarioProfile)}
            </strong>
            {' '}— 검출 용도·시나리오 목록이 이 프로필 기준으로 필터됨.
          </Alert>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* 기본 정보 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>기본 정보</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="코드"
                name="code"
                value={cctvFormData.code || ''}
                onChange={(e) => handleCCTVChange('code', e.target.value)}
                error={!!cctvFormErrors.code}
                helperText={cctvFormErrors.code || ''}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="이름"
                name="name"
                value={cctvFormData.name || ''}
                onChange={(e) => handleCCTVChange('name', e.target.value)}
                error={!!cctvFormErrors.name}
                helperText={cctvFormErrors.name || ''}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="위치"
                name="location"
                value={cctvFormData.location || ''}
                onChange={(e) => handleCCTVChange('location', e.target.value)}
                error={!!cctvFormErrors.location}
                helperText={cctvFormErrors.location || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>카메라 용도</InputLabel>
                <Select
                  value={cctvFormData.purpose || '관제 모니터링'}
                  label="카메라 용도"
                  name="purpose"
                  onChange={(e) => handleCCTVChange('purpose', e.target.value)}
                >
                  {CCTV_PURPOSE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>설치 시점</InputLabel>
                <Select
                  value={cctvFormData.mount_type || 'overhead'}
                  label="설치 시점"
                  name="mount_type"
                  onChange={(e) => handleCCTVChange('mount_type', e.target.value)}
                >
                  {CCTV_MOUNT_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(cctvFormData.rotate_180)}
                    onChange={(e) => handleCCTVChange('rotate_180', e.target.checked)}
                    name="rotate_180"
                  />
                }
                label="영상 표시 180도 회전"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5 }}>
                카메라가 거꾸로 설치된 경우 ON. 브라우저 표시만 돌림(전송·재인코딩 없음).
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel shrink>전용 검출 모델 (.pt)</InputLabel>
                <Select
                  displayEmpty
                  notched
                  label="전용 검출 모델 (.pt)"
                  name="ai_detector_model"
                  value={String(cctvFormData.ai_detector_model || '')}
                  onChange={(e) =>
                    handleCCTVChange('ai_detector_model', e.target.value)
                  }
                  renderValue={(v) =>
                    v ? String(v) : '시설/시스템 기본 사용'
                  }
                >
                  <MenuItem value="">시설/시스템 기본 사용</MenuItem>
                  {String(cctvFormData.ai_detector_model || '') &&
                  !yoloModelFiles.includes(
                    String(cctvFormData.ai_detector_model)
                  ) ? (
                    <MenuItem value={String(cctvFormData.ai_detector_model)}>
                      {String(cctvFormData.ai_detector_model)} (목록에 없음)
                    </MenuItem>
                  ) : null}
                  {yoloModelFiles.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                이 카메라만 다른.pt를 쓸 때 지정. 추론 파이프라인은 시설 설정을 따름.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(25, 118, 210, 0.04)',
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.dark',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }}
              >
                <InputLabel
                  sx={{
                    color: 'primary.main',
                    fontWeight: 700,
                    '&.Mui-focused': { color: 'primary.dark' },
                    '&.MuiInputLabel-shrink': { color: 'primary.main', fontWeight: 700 },
                  }}
                >
                  검출 용도
                </InputLabel>
                <Select
                  value={
                    cctvFormData.detection_purpose ||
                    defaultPurposeForProfile(scenarioProfile)
                  }
                  label="검출 용도"
                  name="detection_purpose"
                  onChange={(e) =>
                    handleCCTVChange('detection_purpose', e.target.value)
                  }
                >
                  {purposeSelectOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                      {option.hint ? ` — ${option.hint}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                sx={{ mt: 0.5, color: 'primary.dark', fontWeight: 600 }}
                display="block"
              >
                시설 유형에 맞는 목록. 용도를 고르면 위험 시나리오(복합 포함) 자동 지정.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(25, 118, 210, 0.04)',
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.dark',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }}
              >
                <InputLabel
                  sx={{
                    color: 'primary.main',
                    fontWeight: 700,
                    '&.Mui-focused': { color: 'primary.dark' },
                    '&.MuiInputLabel-shrink': { color: 'primary.main', fontWeight: 700 },
                  }}
                >
                  위험 시나리오(고급)
                </InputLabel>
                <Select
                  value={
                    cctvFormData.detection_scenario || DEFAULT_DETECTION_SCENARIO
                  }
                  label="위험 시나리오(고급)"
                  name="detection_scenario"
                  onChange={(e) =>
                    handleCCTVChange('detection_scenario', e.target.value)
                  }
                >
                  {scenarioSelectOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                sx={{ mt: 0.5, color: 'primary.dark', fontWeight: 600 }}
                display="block"
              >
                직접 바꾸면 검출 용도가「직접 지정」으로 바뀜.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>상태</InputLabel>
                <Select
                  value={getStatusDisplayValue(cctvFormData.status)}
                  label="상태"
                  name="status"
                  onChange={(e) => handleCCTVChange('status', e.target.value)}
                >
                  {CCTV_STATUS_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* 위치 및 연결 정보 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>연결 정보</Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField
                  fullWidth
                  label="스트림 URL (현장 IP / RTSP)"
                  name="stream_url"
                  value={cctvFormData.stream_url || ''}
                  onChange={(e) => handleCCTVChange('stream_url', e.target.value)}
                  error={!!cctvFormErrors.stream_url}
                  helperText={
                    cctvFormErrors.stream_url ||
                    'RTSP·HTTP 미리보기: 현장 PC는 DataGateway만 켜 두면 로컬(같은 대역) 우선. 전송 시작은 필요 없음.'
                  }
                  required
                />
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PreviewIcon />}
                  onClick={() => {
                    if (cctvFormData.stream_url) {
                      handlePreviewStreamUrl(cctvFormData.stream_url);
                    } else {
                      onSnackbarMessage('스트림 URL을 입력', 'error');
                    }
                  }}
                  title="미리보기 (스트림 URL 기준)"
                  sx={{
                    minWidth: '120px',
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  미리보기
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  siteGatewayEnabled
                    ? 'Playback URL (중앙 관제 · 게이트웨이)'
                    : '재생 주소 (게이트웨이 OFF · stream_url 사용)'
                }
                name="playback_url"
                value={
                  siteGatewayEnabled
                    ? cctvFormData.playback_url || ''
                    : cctvFormData.stream_url || ''
                }
                placeholder={
                  siteGatewayEnabled
                    ? '게이트웨이 ON·RTSP 연결 후 자동 입력'
                    : '위에 입력한 스트림 URL이 관제·미리보기에 사용됨'
                }
                InputProps={{ readOnly: true }}
                helperText={
                  siteGatewayEnabled
                    ? '현장 게이트웨이가 등록한 HLS 주소. 직접 수정하지 않음.'
                    : '게이트웨이 OFF라 별도 playback_url은 없음. 중앙 관제·미리보기는 위 스트림 URL을 그대로 사용.'
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
                위험 감지 (이 카메라 영상 기준 픽셀)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {POOL_RISK_MOTION_CCTV_SECTION_NOTE}
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2" component="div">
                  {POOL_RISK_MOTION_CCTV_TUNING_SUMMARY}
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={cctvFormData.risk_use_pool_motion_defaults !== false}
                    onChange={(e) => {
                      const usePool = e.target.checked;
                      handleCCTVChange('risk_use_pool_motion_defaults', usePool);
                      if (usePool) {
                        setCctvMotionPreset('pool_motion');
                      } else {
                        setCctvMotionPreset(
                          inferCctvMotionOverridePreset(
                            cctvFormData.risk_detection_sensitivity,
                            cctvFormData.risk_detection_stillness_sec
                          )
                        );
                      }
                    }}
                  />
                }
                label="시설 motion 기본값 사용 (민감도·정지·유형 임계)"
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                {POOL_RISK_MOTION_HINTS.cctvUsePool}
              </Typography>
              {cctvFormData.risk_use_pool_motion_defaults !== false ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    현재 이 카메라는 시설 정보 → 수정 → 「Motion 위험 감지」 값을 사용. (민감도{' '}
                    {resolvedPoolMotion.sensitivity}, 정지 {resolvedPoolMotion.stillness_threshold_sec}
                    초, 통합 임계 {resolvedPoolMotion.combined_score_threshold})
                  </Typography>
                </Alert>
              ) : null}
            </Grid>
            {cctvFormData.risk_use_pool_motion_defaults === false ? (
              <Grid item xs={12}>
                <DetectionLevelPicker
                  variant="cctvMotion"
                  legend="Motion 검출 성향 · 8단계 (이 카메라만)"
                  layout="select"
                  value={
                    cctvMotionPreset === 'custom'
                      ? 'custom'
                      : cctvMotionPreset === 'pool_motion'
                        ? '5'
                        : cctvMotionPreset
                  }
                  onSelectLevel={(level) => {
                    setCctvMotionPreset(level);
                    const p = applyCctvMotionOverridePreset(level);
                    setCCTVFormData((prev) => ({
                      ...prev,
                      risk_detection_sensitivity: p.risk_detection_sensitivity,
                      risk_detection_stillness_sec: p.risk_detection_stillness_sec,
                    }));
                  }}
                  onSelectCustom={() => setCctvMotionPreset('custom')}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  단계를 바꾸면 아래 민감도·정지 시간이 갱신됨. 통합 점수·유형 임계는 시설 Motion
                  설정을 그대로 따름.
                </Typography>
                {cctvMotionPreset === 'custom' ? (
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                    숫자를 직접 수정한 상태. 위에서 단계를 다시 고르면 일괄 적용됨.
                  </Typography>
                ) : null}
              </Grid>
            ) : null}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="민감도 (0~1)"
                name="risk_detection_sensitivity"
                type="number"
                inputProps={{ step: 0.05, min: 0, max: 1 }}
                helperText={POOL_RISK_MOTION_HINTS.cctvSensitivity}
                FormHelperTextProps={POOL_RISK_MOTION_HELPER_PROPS}
                value={cctvFormData.risk_detection_sensitivity ?? ''}
                onChange={(e) => {
                  setCctvMotionPreset('custom');
                  handleCCTVChange('risk_detection_sensitivity', e.target.value);
                }}
                disabled={cctvFormData.risk_use_pool_motion_defaults !== false}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="정지 기준 시간(초)"
                name="risk_detection_stillness_sec"
                type="number"
                inputProps={{ step: 1, min: 1 }}
                helperText={POOL_RISK_MOTION_HINTS.cctvStillness}
                FormHelperTextProps={POOL_RISK_MOTION_HELPER_PROPS}
                value={cctvFormData.risk_detection_stillness_sec ?? ''}
                onChange={(e) => {
                  setCctvMotionPreset('custom');
                  handleCCTVChange('risk_detection_stillness_sec', e.target.value);
                }}
                disabled={cctvFormData.risk_use_pool_motion_defaults !== false}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                물(감시) 구역 · 관제 영역
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {CCTV_RISK_ZONES_COORDS_GUIDE} {POOL_RISK_MOTION_HINTS.cctvZones}
                {' '}등록 시 위 스트림 URL(RTSP) 스냅샷 위에 그림. 저장 후 관제 HLS로도 편집 가능.
                {' '}구역을 저장하면 AI는 그 안만 추론하고, 밖 사람 박스는 관제에 표시하지 않음.
              </Typography>
              <RiskZoneEditor
                streamUrl={riskZoneEditorStreamUrl}
                whepUrl={riskZoneEditorWhepUrl}
                preferSnapshot={!(Number(editingCCTV?.id) > 0)}
                zonesJson={cctvFormData.risk_detection_zones_json || CCTV_RISK_NEW_DEFAULTS.zonesJson}
                onZonesJsonChange={(json) => handleCCTVChange('risk_detection_zones_json', json)}
              />
              <TextField
                fullWidth
                sx={{ mt: 1 }}
                label="구역 JSON (고급)"
                name="risk_detection_zones_json"
                multiline
                minRows={2}
                placeholder="[[x1,y1,x2,y2], ...]"
                helperText="위 편집기와 동기화됨. 직접 수정할 수도 있음"
                value={cctvFormData.risk_detection_zones_json || ''}
                onChange={(e) => handleCCTVChange('risk_detection_zones_json', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'error.main' }}>
                특별관리구역 (빨간 박스)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {CCTV_RISK_ZONES_COORDS_GUIDE} 이 구역에 사람이 오래 머물면 AI가 더 엄격하게{' '}
                <strong>특별관리구역 체류</strong> 위험으로 처리. 물(감시) 구역과 별개.
              </Typography>
              <RiskZoneEditor
                accent="red"
                streamUrl={riskZoneEditorStreamUrl}
                whepUrl={riskZoneEditorWhepUrl}
                preferSnapshot={!(Number(editingCCTV?.id) > 0)}
                zonesJson={
                  cctvFormData.special_management_zones_json ||
                  CCTV_RISK_NEW_DEFAULTS.specialZonesJson
                }
                onZonesJsonChange={(json) =>
                  handleCCTVChange('special_management_zones_json', json)
                }
              />
              <TextField
                fullWidth
                sx={{ mt: 1 }}
                label="특별관리구역 JSON (고급)"
                name="special_management_zones_json"
                multiline
                minRows={2}
                placeholder="[[x1,y1,x2,y2], ...]"
                helperText="위 빨간 박스 편집기와 동기화됨"
                value={cctvFormData.special_management_zones_json || ''}
                onChange={(e) =>
                  handleCCTVChange('special_management_zones_json', e.target.value)
                }
              />
              <TextField
                fullWidth
                sx={{ mt: 1.5 }}
                label={`특별관리구역 체류 임계(초) · 시설 기본값 ${facilitySpecialZoneDwellSec}초`}
                name="special_zone_dwell_sec"
                type="number"
                inputProps={{ min: 3, max: 600, step: 1 }}
                placeholder={`시설 기본값 ${facilitySpecialZoneDwellSec}`}
                helperText={`비우면 시설 기본값 ${facilitySpecialZoneDwellSec}초를 사용. (시설→시스템 「임계 설정」 승계)`}
                value={
                  cctvFormData.special_zone_dwell_sec === undefined ||
                  cctvFormData.special_zone_dwell_sec === null
                    ? ''
                    : cctvFormData.special_zone_dwell_sec
                }
                onChange={(e) => handleCCTVChange('special_zone_dwell_sec', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 0.5, mt: 2 }}>
                사람 검출 (YOLO · 이 카메라만)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {AI_DETECTION_HINTS.cctvOverrideIntro} {AI_DETECTION_HINTS.cctvConfEmpty}
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                적용 우선순위: <strong>CCTV 입력값</strong> → 시설 「검출 신뢰도 하한/연속 프레임」 → 시스템 「임계 설정」.
              </Alert>
              <Alert severity="success" sx={{ mb: 1 }}>
                현재 시설 기본값: 검출 신뢰도 하한 {facilityDetectionConfHint}, 연속 프레임 확정 {facilityDetectionHitsHint}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <DetectionLevelPicker
                variant="pool"
                legend="검출 성향 빠른 설정 · 8단계"
                layout="select"
                value={
                  cctvDetectionPreset === 'pool_default'
                    ? 'pool_default'
                    : cctvDetectionPreset === 'custom'
                      ? 'custom'
                      : cctvDetectionPreset
                }
                onSelectLevel={(level) => {
                  setCctvDetectionPreset(level);
                  const p = applyPoolDetectionPreset(level);
                  setCCTVFormData((prev) => ({
                    ...prev,
                    ai_detection_conf: p.ai_detection_conf,
                    ai_detection_min_consecutive_hits: p.ai_detection_min_consecutive_hits,
                  }));
                }}
                onSelectCustom={() => setCctvDetectionPreset('custom')}
                emptyOptionLabel="시설 기본 사용 (비우기)"
                onSelectEmpty={() => {
                  setCctvDetectionPreset('pool_default');
                  setCCTVFormData((prev) => ({
                    ...prev,
                    ai_detection_conf: '',
                    ai_detection_min_consecutive_hits: '',
                  }));
                }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                8단계를 고르면 아래 신뢰도·연속 프레임이 즉시 채워짐. 「시설 기본 사용」은 두 칸을 비워
                시설 YOLO 기본을 따름.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`검출 신뢰도 하한 (선택) · 시설 기본 ${facilityDetectionConfHint}`}
                name="ai_detection_conf"
                type="number"
                placeholder={`비우면 시설 기본 (${facilityDetectionConfHint})`}
                inputProps={{ step: 0.05, min: 0.05, max: 0.99 }}
                helperText={`${AI_DETECTION_HINTS.conf}\n비우면 시설 기본값 ${facilityDetectionConfHint} 사용`}
                FormHelperTextProps={{ sx: { whiteSpace: 'pre-line' } }}
                value={
                  cctvFormData.ai_detection_conf === '' || cctvFormData.ai_detection_conf === undefined
                    ? ''
                    : cctvFormData.ai_detection_conf
                }
                onChange={(e) => {
                  setCctvDetectionPreset('custom');
                  handleCCTVChange('ai_detection_conf', e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`연속 프레임 확정 (선택) · 시설 기본 ${facilityDetectionHitsHint}`}
                name="ai_detection_min_consecutive_hits"
                type="number"
                placeholder={`비우면 시설 기본 (${facilityDetectionHitsHint})`}
                inputProps={{ step: 1, min: 1, max: 30 }}
                helperText={`${AI_DETECTION_HINTS.minHits}\n${AI_DETECTION_HINTS.cctvMinHitsEmpty}\n시설 기본값 ${facilityDetectionHitsHint}`}
                FormHelperTextProps={{ sx: { whiteSpace: 'pre-line' } }}
                value={
                  cctvFormData.ai_detection_min_consecutive_hits === '' ||
                  cctvFormData.ai_detection_min_consecutive_hits === undefined
                    ? ''
                    : cctvFormData.ai_detection_min_consecutive_hits
                }
                onChange={(e) => {
                  setCctvDetectionPreset('custom');
                  handleCCTVChange('ai_detection_min_consecutive_hits', e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="monitor-filter-strength-label">관제 오탐 필터 강도 (이 카메라만)</InputLabel>
                <Select
                  labelId="monitor-filter-strength-label"
                  label="관제 오탐 필터 강도 (이 카메라만)"
                  value={
                    cctvFormData.monitor_filter_strength === '' ||
                    cctvFormData.monitor_filter_strength === undefined ||
                    cctvFormData.monitor_filter_strength === null
                      ? ''
                      : String(cctvFormData.monitor_filter_strength)
                  }
                  onChange={(e: SelectChangeEvent<string>) => {
                    const raw = e.target.value;
                    handleCCTVChange(
                      'monitor_filter_strength',
                      raw === '' ? '' : Number(raw)
                    );
                  }}
                >
                  <MenuItem value="">기본(균형)</MenuItem>
                  <MenuItem value="0">완화 (사람 놓침 최소, 오탐 증가 가능)</MenuItem>
                  <MenuItem value="1">균형 (권장)</MenuItem>
                  <MenuItem value="2">강화 (바닥/물결 오탐 억제, 미탐 증가 가능)</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                관제 화면 표시 필터 강도. 값이 높을수록 정적 구조물/물결 오탐이 더 강하게 억제됨.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="tracker-type-label">추적기 타입 (이 카메라만)</InputLabel>
                <Select
                  labelId="tracker-type-label"
                  label="추적기 타입 (이 카메라만)"
                  value={String(cctvFormData.tracker_type || 'native')}
                  onChange={(e: SelectChangeEvent<string>) => {
                    handleCCTVChange('tracker_type', e.target.value || 'native');
                  }}
                >
                  <MenuItem value="native">Native (기본, 경량)</MenuItem>
                  <MenuItem value="bytetrack">ByteTrack 성향 (연결 유지 강화)</MenuItem>
                  <MenuItem value="botsort">BoT-SORT 성향 (안정성 강화)</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                카메라 상황에 따라 ID 유지/오탐 억제 성향을 다르게 적용.
              </Typography>
            </Grid>

            {/* 장비 정보 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>장비 정보</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="모델"
                name="model"
                value={cctvFormData.model || ''}
                onChange={(e) => handleCCTVChange('model', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="제조사"
                name="manufacturer"
                value={cctvFormData.manufacturer || ''}
                onChange={(e) => handleCCTVChange('manufacturer', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={cctvFormData.is_active === true}
                    onChange={(e) => handleCCTVChange('is_active', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight="medium" mr={1}>
                      CCTV 활성화
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color={cctvFormData.is_active ? "success.main" : "text.secondary"}
                      sx={{ 
                        fontWeight: 'bold',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        backgroundColor: cctvFormData.is_active ? '#E6FFEA' : '#F0F0F0'
                      }}
                    >
                      {cctvFormData.is_active ? '활성화됨' : '비활성화됨'}
                    </Typography>
                  </Box>
                }
                sx={{ mt: 2 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                CCTV를 활성화하면 모니터링에 표시되고, 비활성화하면 표시되지 않음.
              </Typography>
            </Grid>

            {/* 유지보수 정보 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>유지보수 정보</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="설치일"
                name="created_at"
                type="datetime-local"
                value={cctvFormData.created_at || ''}
                onChange={(e) => handleCCTVChange('created_at', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="변경일"
                type="datetime-local"
                name="updated_at"
                value={cctvFormData.updated_at || ''}
                onChange={(e) => handleCCTVChange('updated_at', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="마지막 유지보수"
                type="datetime-local"
                name="last_maintenance_date"
                value={cctvFormData.last_maintenance_date || ''}
                onChange={(e) => handleCCTVChange('last_maintenance_date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="다음 유지보수"
                type="datetime-local"
                name="next_maintenance_date"
                value={cctvFormData.next_maintenance_date || ''}
                onChange={(e) => handleCCTVChange('next_maintenance_date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* 추가 정보 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>추가 정보</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="설명"
                name="description"
                multiline
                rows={4}
                value={cctvFormData.description || ''}
                onChange={(e) => handleCCTVChange('description', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCCTVDialog} color="primary" disabled={isSubmitting}>
            취소
          </Button>
          <Button 
            onClick={handleCCTVSubmit} 
            color="primary" 
            disabled={isSubmitting}
            variant="contained"
          >
            {isSubmitting ? <CircularProgress size={24} /> : editingCCTV?.id ? '수정' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // CCTV 목록 렌더링
  const renderCCTVs = (cctvList: Cctv[]) => {
    // 상태값 표시 함수
    const getStatusDisplay = (status: CctvStatus | string): string => {
      if (!status) return '정상';
      const koreanStatus = convertStatusToKorean(status);
      return typeof koreanStatus === 'string' ? koreanStatus : '정상';
    };

    // CCTV 목록을 코드 순으로 정렬
    const sortedCctvList = [...cctvList].sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">CCTV 관리</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={() => handleOpenCCTVDialog()}
          >
            CCTV 추가
          </Button>
        </Box>

        {poolId && Number(poolId) > 0 ? (
          <PoolCctvThresholdUnifyCard
            compact
            poolId={Number(poolId)}
            specialZoneDwellSec={facilitySpecialZoneDwellSec}
            detectionConfLabel={facilityDetectionConfHint}
            detectionHitsLabel={facilityDetectionHitsHint}
            cctvCount={sortedCctvList.length}
            onDone={() => {
              void fetchCCTVs();
              onSnackbarMessage?.(
                'CCTV 임계를 시설 기준으로 통일함',
                'success'
              );
            }}
          />
        ) : null}
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>CCTV 정보</TableCell>
                <TableCell>위치</TableCell>
                <TableCell>용도</TableCell>
                <TableCell>스트림 URL (현장)</TableCell>
                <TableCell>
                  {siteGatewayEnabled ? 'Playback URL (관제)' : '재생 주소 (stream_url)'}
                </TableCell>
                <TableCell>상태</TableCell>
                <TableCell>활성화</TableCell>
                <TableCell align="right">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCctvList.map((cctv) => (
                <TableRow key={cctv.id}>
                  <TableCell>
                    {cctv.code} ({cctv.name})
                  </TableCell>
                  <TableCell>{cctv.location}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {cctv.purpose || '일반 모니터링'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        maxWidth: 260,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={cctv.stream_url || ''}
                    >
                      {cctv.stream_url || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        maxWidth: 260,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color:
                          (siteGatewayEnabled
                            ? cctv.playback_url
                            : cctv.stream_url)
                            ? 'text.primary'
                            : 'text.secondary',
                      }}
                      title={
                        siteGatewayEnabled
                          ? cctv.playback_url || ''
                          : cctv.stream_url || ''
                      }
                    >
                      {siteGatewayEnabled
                        ? cctv.playback_url || '게이트웨이 연결 후 표시'
                        : cctv.stream_url || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {cctv.status && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 
                              cctv.status === 'ACTIVE' ? '#006400' : 
                              cctv.status === 'MAINTENANCE' ? '#7A4F01' : 
                              cctv.status === 'INACTIVE' ? '#333333' : 
                              cctv.status === 'ERROR' ? '#7A0000' : '#000000',
                            fontWeight: 'bold',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            backgroundColor: 
                              cctv.status === 'ACTIVE' ? '#E6FFEA' : 
                              cctv.status === 'MAINTENANCE' ? '#FFF6E6' : 
                              cctv.status === 'INACTIVE' ? '#F0F0F0' : 
                              cctv.status === 'ERROR' ? '#FFEBEB' : 'transparent',
                          }}
                        >
                          {getStatusDisplay(cctv.status)}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: cctv.is_active ? '#006400' : '#333333',
                          fontWeight: 'bold',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          backgroundColor: cctv.is_active ? '#E6FFEA' : '#F0F0F0',
                        }}
                      >
                        {cctv.is_active ? '활성화' : '비활성화'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handlePreviewStream(cctv)}
                      title="미리보기"
                    >
                      <PreviewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(cctv)}
                      title="수정"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteCCTV(cctv.id)}
                      title="삭제"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // 미리보기 Dialog — 빈 목록/목록 있음 공통 (등록 시에도 반드시 마운트)
  const renderPreviewDialog = () => (
    <Dialog
      open={!!previewCctv}
      onClose={handleClosePreview}
      maxWidth={false}
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      hideBackdrop={false}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: 'min(1200px, 92vw)' },
          maxWidth: { xs: '100%', md: 'min(1200px, 92vw)' },
          maxHeight: { xs: '100%', md: '92vh' },
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {previewCctv?.name} - 미리보기
          </Typography>
          <IconButton onClick={handleClosePreview} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: { xs: '60vh', md: '72vh' },
          minHeight: { xs: 280, md: 480 },
          width: '100%',
        }}
      >
        {previewCctv && (
          <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
            <CctvPreview
              key={`preview-${previewCctv.id ?? 'new'}-${previewCctv.stream_url || ''}`}
              cctv={previewCctv}
              isInitiallyMuted={true}
              onClose={handleClosePreview}
              siteGatewayEnabled={siteGatewayEnabled}
              forceSnapshot={!(Number(previewCctv.id) > 0)}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );

  // 메인 UI 렌더링
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // CCTV 데이터가 없거나 비어있는 경우
  if (cctvs.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">CCTV 관리</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={() => handleOpenCCTVDialog()}
          >
            CCTV 추가
          </Button>
        </Box>
        
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            등록된 CCTV가 없음. 새 CCTV를 추가
          </Typography>
        </Paper>
        {renderCCTVDialog()}
        {renderPreviewDialog()}
      </Box>
    );
  }

  // 정상적인 CCTV 데이터 표시
  return (
    <Box>
      {renderCCTVs(cctvs)}
      {renderCCTVDialog()}
      {renderPreviewDialog()}
    </Box>
  );
};

export default CCTVManagement; 